// R8 — **THE MY ROOM COLUMN LAYOUT, AND THE ARITHMETIC NOTHING COULD RUN.** (Extracted by HOME-1
// FINAL, which needed the ceiling paid and took it in this ledger's favourite currency: the thing
// that had to move is the thing nothing could reach.)
//
// Every panel a member arranges in My Room is laid out by ONE expression —
// `max(1, min(4, floor(width / 336)))` — and inside an eleven-thousand-line file no `.mjs` gate
// could load it, so the one decision that says whether her room is one column or four was held by
// nothing at all. `panelColumns` is that law, pure and exported, so a fixture RUNS it: the floor at
// a phone width, the ceiling on a wall-sized monitor, and a container that has not measured itself
// yet (`clientWidth === 0` during the first paint, which is the input that would otherwise hand her
// a room with ZERO columns and draw nothing).
//
// The component itself holds three hooks, so it is the `TierCards` / `MicButton` class rather than a
// hookless leaf a gate CALLS — **the reach it buys is the IMPORT plus the law above it**. Moved
// VERBATIM otherwise: the tree it renders is byte-identical, so this round carries no render risk.
import { Children, useEffect, useRef, useState, type ReactNode } from "react";

/** The width one column needs before a second one is worth having. */
export const PANEL_COLUMN_WIDTH = 336;
export const PANEL_COLUMNS_MIN = 1;
export const PANEL_COLUMNS_MAX = 4;

/**
 * How many columns a container of this width gets.
 *
 * **THE FLOOR IS WHY THIS IS A FUNCTION AND NOT AN EXPRESSION.** A container that has not been
 * measured yet answers `clientWidth === 0`, and `floor(0 / 336)` is 0 — which builds ZERO column
 * arrays, drops every panel on the floor, and renders an EMPTY ROOM with no error anywhere. That
 * state is real on the first paint of every visit.
 */
export function panelColumns(width: number): number {
  // R53/R136/R137 — presence, never coercion: `Number(true)` is 1, and a width that is not a real
  // number must fall to the floor rather than laying her room out against a fabricated one.
  const w = typeof width === "number" && Number.isFinite(width) && width > 0 ? width : 0;
  return Math.max(PANEL_COLUMNS_MIN, Math.min(PANEL_COLUMNS_MAX, Math.floor(w / PANEL_COLUMN_WIDTH)));
}

export function MasonryPanels({ children }: { children: ReactNode }) {
  const items = Children.toArray(children);
  const ref = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(PANEL_COLUMNS_MIN + 2);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const compute = () => setCols(panelColumns(el.clientWidth));
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const columns: ReactNode[][] = Array.from({ length: cols }, () => []);
  items.forEach((child, i) => columns[i % cols].push(child));
  return (
    <div className="room-cols" ref={ref}>
      {columns.map((col, i) => (
        <div className="room-col" key={i}>{col}</div>
      ))}
    </div>
  );
}
